/**
 * Daily Insights API Client
 * 
 * Handles communication with Next.js API routes for insights.
 * Authentication is handled server-side via API routes.
 */

import { 
  DailyInsight, 
  TodayInsightResponse, 
  InsightsHistoryResponse 
} from '@/types/insights';
import { markInsightAsViewed as markInsightAsViewedLocal } from '@/lib/insights-storage';

/**
 * Insights API client - calls Next.js API routes (no direct session needed)
 */
export class InsightsAPIClient {
  /**
   * Make request to Next.js API route (authentication handled server-side)
   */
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = endpoint;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include', // Include cookies for session authentication
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        let errorData: any = null;
        
        try {
          errorData = await response.json();
          console.error(`[Insights API] Response error data:`, JSON.stringify(errorData, null, 2));
          
          // Try multiple paths to extract error message
          errorMessage = errorData.error?.message 
            || errorData.error 
            || errorData.message 
            || errorData.detail
            || JSON.stringify(errorData)
            || errorMessage;
        } catch (e) {
          // If JSON parsing fails, try to get text
          try {
            const textError = await response.text();
            console.error(`[Insights API] Response error text:`, textError);
            errorMessage = textError || errorMessage;
          } catch (textError) {
            console.error(`[Insights API] Could not parse error response`);
          }
        }
        
        console.error(`[Insights API] Request failed: ${endpoint}`, {
          status: response.status,
          statusText: response.statusText,
          errorMessage,
          errorData
        });
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error(`[Insights API] Error calling ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Fetch today's daily insight
   */
  async getTodayInsights(viewAll: boolean = false): Promise<DailyInsight | null> {
    try {
      const response = await this.makeRequest<TodayInsightResponse>(
        `/api/insights/today${viewAll ? '?viewAll=true' : ''}`
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      // Silently return null on error to prevent console spam
      // Error already logged in makeRequest
      return null;
    }
  }

  /**
   * Fetch insights history with optional limit
   * @param limit - Number of insights to fetch (default: 7)
   * @param offset - Number of insights to skip (default: 0)
   */
  async getInsightsHistory(limit: number = 7, offset: number = 0): Promise<DailyInsight[]> {
    try {
      const response = await this.makeRequest<InsightsHistoryResponse>(
        `/api/insights?limit=${limit}&offset=${offset}`
      );
      
      console.log('[Insights API Client] Full response:', JSON.stringify(response, null, 2));
      console.log('[Insights API Client] Response success:', response.success);
      console.log('[Insights API Client] Response items:', response.items);
      console.log('[Insights API Client] Response data:', (response as any).data);
      
      // Handle both old format (data array) and new format (items array)
      if (response.success) {
        const result = response.items || (response as any).data || [];
        
        console.log('[Insights API Client] Extracted result:', result);
        console.log('[Insights API Client] Result type:', typeof result);
        console.log('[Insights API Client] Is array?:', Array.isArray(result));
        
        // Ensure result is an array
        if (!Array.isArray(result)) {
          console.warn('[Insights API Client] Response is not an array, type:', typeof result);
          console.warn('[Insights API Client] Value:', result);
          return [];
        }
        
        console.log('[Insights API Client] Returning', result.length, 'insights');
        return result;
      }
      
      console.warn('[Insights API Client] Response success=false, returning empty array');
      return [];
    } catch (error) {
      console.error('[Insights API Client] Failed to fetch insights history:', error);
      // Return empty array instead of throwing to prevent component crashes
      return [];
    }
  }

  /**
   * Mark an insight as viewed
   * @param insightId - ID of the insight to mark as viewed
   * @param dateKey - Optional date key for local storage
   * 
   * Uses local storage as primary tracking mechanism.
   * Backend API call is optional and fails silently.
   */
  async markInsightAsViewed(insightId: string, dateKey?: string): Promise<void> {
    // Always store locally first (primary tracking)
    const date = dateKey || new Date().toISOString().split('T')[0];
    markInsightAsViewedLocal(insightId, date);
    
    // Try to sync with backend (optional, non-blocking)
    try {
      await this.makeRequest(`/api/insights/${insightId}/viewed`, {
        method: 'POST',
      });
    } catch (error) {
      // Silently catch error - local storage is the source of truth
      // Backend endpoint not yet implemented, this is expected
      console.debug('[Insights API] Backend sync skipped (endpoint not available)');
    }
  }
}

/**
 * Create insights API client
 * Convenience function for use in components
 */
export function createInsightsClient(): InsightsAPIClient {
  return new InsightsAPIClient();
}
